export type LaunchConfig = {
  strategy?: string
  target_url?: string
  open_in_new_tab?: boolean
  open_new_tab?: boolean
  next?: string
  next_path?: string
}

export type BotMeta = {
  vendor?: string
  bot_type?: string
  session_type?: string
  embed_code?: string
  agent_id?: string
  code_runner_enabled?: boolean
  launch_config?: LaunchConfig
  ui_settings?: {
    theme?: string
    allow_file_uploads?: boolean
    enable_zotero_sync?: boolean
    [key: string]: unknown
  }
  voice_settings?: {
    auto_connect?: boolean
    allow_file_uploads?: boolean
  }
  [key: string]: unknown
}

type BotIdentityLike = {
  bot_id?: string | null
  meta?: BotMeta | null
}

export function isVoiceEmbedAgent(meta?: BotMeta | null): boolean {
  if (!meta) return false
  const vendor = meta.vendor?.toLowerCase()
  const botType = meta.bot_type?.toLowerCase()
  return vendor === '11labs' && botType === 'voice_agent_embed' && !!meta.embed_code?.trim()
}

export function isVoiceSDKAgent(meta?: BotMeta | null): boolean {
  if (!meta) return false
  const vendor = meta.vendor?.toLowerCase()
  const botType = meta.bot_type?.toLowerCase()
  // Check for SDK type and presence of agent_id
  return vendor === '11labs' && botType === 'voice_agent_sdk' && !!(meta as any).agent_id
}

export function isAnyVoiceAgent(meta?: BotMeta | null): boolean {
  return isVoiceEmbedAgent(meta) || isVoiceSDKAgent(meta)
}

export function usesHandoffRedirect(meta?: BotMeta | null): boolean {
  return meta?.launch_config?.strategy?.toLowerCase() === 'handoff_redirect' || isScholarLaunchEnabled(meta)
}

export function isExternalAppLaunch(meta?: BotMeta | null): boolean {
  return usesHandoffRedirect(meta) || isScholarResearchAssistant(meta)
}

export function isScholarResearchAssistant(meta?: BotMeta | null): boolean {
  return meta?.session_type?.toLowerCase() === 'research-assistant'
}

export function isScholarLaunchEnabled(meta?: BotMeta | null): boolean {
  return isScholarResearchAssistant(meta) && meta?.code_runner_enabled === true
}

export function isCodeRunnerBot(meta?: BotMeta | null): boolean {
  return meta?.bot_type?.toLowerCase() === 'code_runner'
}

export function resolveCanonicalAssistantId(bot?: BotIdentityLike | null): string | null {
  const agentId = bot?.meta?.agent_id?.trim()
  if (agentId) return agentId

  const botId = bot?.bot_id?.trim()
  return botId || null
}

export function getLaunchHost(meta?: BotMeta | null): string | null {
  const targetUrl = meta?.launch_config?.target_url

  if (!targetUrl) return null

  try {
    return new URL(targetUrl).host
  } catch {
    return null
  }
}

export function shouldOpenLaunchInNewTab(meta?: BotMeta | null): boolean {
  return meta?.launch_config?.open_in_new_tab === true || meta?.launch_config?.open_new_tab === true
}

export function encodeBotIdForRoute(botId: string): string {
  return encodeURIComponent(botId)
}

export function decodeBotIdFromRoute(encodedId: string): string {
  try {
    return decodeURIComponent(encodedId)
  } catch {
    return encodedId
  }
}
